import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
/**
--
category: packages/media-capture
--
**/

import { CaptureModes } from '../constants/CaptureModes';

/**
 * Function to be called to check if the clients browser
 * can support the getUserMedia/Stream, MediaRecorder and AudioContext api's.
 * https://caniuse.com/#search=getusermedia
 * https://caniuse.com/#search=mediarecorder
 * https://caniuse.com/#search=audiocontext
 */
export function canUseMediaCapture(win = window) {
  return canUseUserMedia(win) && canUseMediaRecorder(win) && canUseAudioContext(win);
}
export const CAN_USE_CAMERA_EFFECTS = typeof MediaStreamTrackProcessor === 'function' && typeof MediaStreamTrackGenerator === 'function';

/**
 * Function to be called to check if the clients browser
 * can support the getDisplayMedia/Stream, MediaRecorder and AudioContext api's.
 * https://caniuse.com/#search=getdisplaymedia
 * https://caniuse.com/#search=mediarecorder
 * https://caniuse.com/#search=audiocontext
 */
export function canUseScreenCapture(win = window) {
  return canUseDisplayMedia(win) && canUseMediaRecorder(win);
}
/**
 * Function to be called to format time in seconds to a human readable format.(e.g. 1:30)
 */
export function formatTime(time) {
  if (time && typeof time === 'number' && time < Infinity) {
    const hours = Math.floor(time / 3600);
    let minutes = Math.floor(time / 60) % 60;
    let seconds = Math.floor(time % 60);
    if (minutes < 10 && hours > 0) {
      minutes = `0${minutes}`;
    }
    if (seconds < 10) {
      seconds = `0${seconds}`;
    }
    return [...(hours > 0 ? [hours] : []), minutes, seconds].join(':');
  }
  return '0:00';
}

/**
 * Presents time in mm:ss format
 */
export function formatCaptureTime(time) {
  const minutes = Math.floor(time / 60000);
  const seconds = Math.floor(time % 60000 / 1000);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
function canUseUserMedia(win = window) {
  return !!win.navigator.mediaDevices && typeof win.navigator.mediaDevices.getUserMedia === 'function';
}
function canUseDisplayMedia(win = window) {
  return !!win.navigator.mediaDevices && typeof win.navigator.mediaDevices.getDisplayMedia === 'function';
}
function canUseMediaRecorder(win = window) {
  return typeof win.MediaRecorder === 'function';
}
function canUseAudioContext(win = window) {
  return typeof win.AudioContext === 'function' || typeof win.webkitAudioContext === 'function';
}
export function getCaptureMode({
  screenSharingActive,
  videoDeviceId,
  audioDeviceId
}) {
  if (screenSharingActive) {
    return CaptureModes.ScreenSharing;
  }
  if (videoDeviceId) {
    return CaptureModes.Webcam;
  }
  if (audioDeviceId) {
    return CaptureModes.AudioOnly;
  }
  return CaptureModes.Inactive;
}
export function getTrackerModeByCaptureMode(captureMode) {
  switch (captureMode) {
    case CaptureModes.ScreenSharing:
      return 'studioCapture.screencapture';
    case CaptureModes.Webcam:
      return 'studioCapture.webcam';
    case CaptureModes.AudioOnly:
      return 'studioCapture.audio';
    default:
      // if this is applied something went wrong
      // you can't record video with no inputs
      return 'inactive';
  }
}
export function determineScreenSharingType(stream) {
  const _stream$getVideoTrack = stream.getVideoTracks(),
    _stream$getVideoTrack2 = _slicedToArray(_stream$getVideoTrack, 1),
    track = _stream$getVideoTrack2[0];
  const _track$getCapabilitie = track.getCapabilities(),
    displaySurface = _track$getCapabilitie.displaySurface;
  switch (displaySurface) {
    case 'window':
      return 'window';
    case 'browser':
      return 'tab';
    case 'monitor':
      return 'monitor';
    default:
      return 'none';
  }
}